import { formatMoney } from 'theme';

class SubscriptionTierGrid extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.isBundle = this.dataset.bundle === 'true';

    const perMonthDivisor = { monthly: 1, yearly: 12, '2year': 24 };
    this.tiers = ['monthly', 'yearly', '2year'].reduce((acc, key) => {
      const slug = key === '2year' ? 'year2' : key;
      const price = parseInt(this.dataset[`${slug}Price`], 10) || 0;
      acc[key] = {
        variantId: this.dataset[`${slug}Id`],
        sellingPlanId: this.dataset[`${slug}Plan`],
        price,
        compareAt: parseInt(this.dataset[`${slug}Compare`], 10) || 0,
        save: parseInt(this.dataset[`${slug}Save`], 10) || 0,
        // Floor to match the Liquid SSR per-month (`price | divided_by: N` does
        // integer division), so the tagline and tile foot never differ by 1p.
        perMonth: Math.floor(price / perMonthDivisor[key]),
      };
      return acc;
    }, {});

    this.saveTemplate = this.dataset.saveTemplate || 'Save __AMOUNT__';

    this.tiles = Array.from(this.querySelectorAll('.subscription-tier-grid__tier'));
    this.tiles.forEach((tile) => {
      tile.addEventListener('click', this._onTileClick);
      const radio = tile.querySelector('input[type="radio"]');
      radio?.addEventListener('change', this._onTileClick);
    });

    this.trigger = this.querySelector('[data-tier-trigger]');
    this.triggerTitle = this.querySelector('[data-trigger-title]');
    this.triggerNow = this.querySelector('[data-trigger-now]');
    this.triggerCompare = this.querySelector('[data-trigger-compare]');
    this.currentFoot = this.querySelector('[data-current-foot]');
    this.cardRibbon = this.querySelector('[data-card-ribbon]');

    this.trigger?.addEventListener('click', this._onTriggerClick);
    document.addEventListener('click', this._onDocumentClick);

    // This grid owns the buy-button price when active (theme.js BuyButtons bails
    // for a visible tier grid). Track the selected tier and recompute on warranty
    // toggle so the app price is never dropped.
    this.currentTierKey =
      this.tiles.find((t) => t.querySelector('input[type="radio"]')?.checked)?.dataset.tier ||
      this.tiles[0]?.dataset.tier;
    this.warrantyInput = document.getElementById('WarrantyCheckbox');
    this.warrantyInput?.addEventListener('change', this._reassertPrice);

    // Re-assert the subscription price when the subscription option is re-selected.
    // The one-time option (scanner only) is left to theme.js BuyButtons.
    this.subscriptionRadio = document.getElementById('selling-plan-subscription');
    this.subscriptionRadio?.addEventListener('change', this._reassertPrice);

    if (this._isActive() && this._isSubscriptionSelected() && this.tiers[this.currentTierKey]) {
      this._updateBuyButtonPrice(this.tiers[this.currentTierKey]);
    }
  }

  disconnectedCallback() {
    this.tiles?.forEach((tile) => {
      tile.removeEventListener('click', this._onTileClick);
      tile.querySelector('input[type="radio"]')?.removeEventListener('change', this._onTileClick);
    });
    this.trigger?.removeEventListener('click', this._onTriggerClick);
    document.removeEventListener('click', this._onDocumentClick);
    this.warrantyInput?.removeEventListener('change', this._reassertPrice);
    this.subscriptionRadio?.removeEventListener('change', this._reassertPrice);
  }

  // Active = visible (the A/B variant injection sets display on the element).
  _isActive() {
    return window.getComputedStyle(this).display !== 'none';
  }

  // True when the subscription option is active (vs the one-time / scanner-only
  // option). If there's no one-time option, subscription is always the choice.
  _isSubscriptionSelected() {
    return !this.subscriptionRadio || this.subscriptionRadio.checked;
  }

  // Recompute the buy-button price for the selected tier. Only acts while the grid
  // is active AND a subscription is selected; one-time is owned by theme.js so the
  // scanner-only price can show.
  _reassertPrice = () => {
    if (!this._isActive() || !this._isSubscriptionSelected()) return;
    const tier = this.tiers[this.currentTierKey];
    if (tier) this._updateBuyButtonPrice(tier);
  };

  _onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const isOpen = this.getAttribute('data-mobile-panel') === 'open';
    this._setPanelOpen(!isOpen);
  };

  _onDocumentClick = (event) => {
    if (this.getAttribute('data-mobile-panel') !== 'open') return;
    if (this.contains(event.target)) return;
    this._setPanelOpen(false);
  };

  _setPanelOpen(open) {
    if (open) {
      this.setAttribute('data-mobile-panel', 'open');
      this.trigger?.setAttribute('aria-expanded', 'true');
    } else {
      this.removeAttribute('data-mobile-panel');
      this.trigger?.setAttribute('aria-expanded', 'false');
    }
  }

  _onTileClick = (event) => {
    const tile = event.currentTarget.closest('.subscription-tier-grid__tier');
    if (!tile) return;
    const tierKey = tile.dataset.tier;
    if (!tierKey || !this.tiers[tierKey]) return;
    this.selectTier(tierKey);
  };

  selectTier(tierKey) {
    const tier = this.tiers[tierKey];
    if (!tier) return;
    this.currentTierKey = tierKey;

    // Choosing a tier implies the subscription (scanner + app) purchase option.
    // Select that radio and fire change so the widget enables the bundle's app
    // fields (isPlanApplied / toggleSubscriptionFields); the tier's own variant
    // and plan are set just below in _updateFormInputs.
    if (this.subscriptionRadio && !this.subscriptionRadio.checked) {
      this.subscriptionRadio.checked = true;
      this.subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.tiles.forEach((t) => {
      const isSelected = t.dataset.tier === tierKey;
      t.classList.toggle('subscription-tier-grid__tier--selected', isSelected);
      const radio = t.querySelector('input[type="radio"]');
      if (radio) radio.checked = isSelected;
    });

    this._updateTriggerLabel(tierKey, tier);
    this._updateFormInputs(tier);
    this._updateSubscriptionCardPrice(tier);
    this._updateMobilePricing(tier);
    this._updateTaglinePerMonth(tier);
    this._updateBuyButtonPrice(tier);
    this._dispatchSellingPlanChanged(tier.sellingPlanId);

    if (this.getAttribute('data-mobile-panel') === 'open') {
      this._setPanelOpen(false);
    }
  }

  _updateTriggerLabel(tierKey, tier) {
    const tile = this.tiles.find((t) => t.dataset.tier === tierKey);
    if (!tile) return;

    const title = tile.querySelector('.subscription-tier-grid__tier-title')?.textContent.trim() || '';
    if (this.triggerTitle) this.triggerTitle.textContent = title;
    if (this.triggerNow) this.triggerNow.textContent = formatMoney(tier.price);

    const hasSale = tier.compareAt > tier.price;
    if (this.triggerCompare) {
      if (hasSale) {
        this.triggerCompare.textContent = formatMoney(tier.compareAt);
        this.triggerCompare.hidden = false;
      } else {
        this.triggerCompare.textContent = '';
        this.triggerCompare.hidden = true;
      }
    }

    const foot = tile.dataset.tierFoot || '';
    if (this.currentFoot) this.currentFoot.textContent = foot;

    if (this.cardRibbon) {
      const ribbonText = tile.dataset.tierRibbon || '';
      const ribbonStyle = tile.dataset.tierRibbonStyle || '';
      this.cardRibbon.textContent = ribbonText;
      this.cardRibbon.hidden = !ribbonText;
      this.cardRibbon.classList.remove(
        'subscription-tier-grid__card-ribbon--popular',
        'subscription-tier-grid__card-ribbon--value',
        'subscription-tier-grid__card-ribbon--exclusive'
      );
      if (ribbonStyle) {
        this.cardRibbon.classList.add(`subscription-tier-grid__card-ribbon--${ribbonStyle}`);
      }
    }
  }

  _getForm() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  // Lazily parse and cache the shared #PricesData JSON blob (product form prices).
  _getPricesData() {
    if (this._pricesData !== undefined) return this._pricesData;
    this._pricesData = null;
    try {
      const el = document.getElementById('PricesData');
      if (el) this._pricesData = JSON.parse(el.textContent);
    } catch (e) {}
    return this._pricesData;
  }

  // On a bundle PDP the customer-facing total is the base product price PLUS the
  // selected subscription tier. The tier data attributes only carry the
  // subscription (app) price, so shared widget price displays must add the base
  // to stay consistent with the Liquid SSR. Returns 0 for non-bundle products.
  _bundleBaseAddend() {
    if (!this.isBundle) return 0;
    const data = this._getPricesData();
    return data?.baseProductPrices?.current || 0;
  }

  _updateFormInputs(tier) {
    const form = this._getForm();
    if (!form) return;

    const idName = this.isBundle ? 'items[1][id]' : 'id';
    const planName = this.isBundle ? 'items[1][selling_plan]' : 'selling_plan';

    const idInput = this._findFormInput(form, idName);
    const planInput = this._findFormInput(form, planName);

    if (idInput) {
      idInput.disabled = false;
      idInput.value = tier.variantId;
    }
    if (planInput) {
      planInput.disabled = false;
      planInput.value = tier.sellingPlanId;
    }
  }

  _findFormInput(form, name) {
    const escaped = name.replace(/(["\\])/g, '\\$1');
    return (
      form.querySelector(`input[name="${escaped}"]`) ||
      (this.formId && document.querySelector(`input[name="${escaped}"][form="${this.formId}"]`)) ||
      null
    );
  }

  _updateSubscriptionCardPrice(tier) {
    const card = document.querySelector('.subscription__subs .subscription-label-right');
    if (!card) return;

    // For bundles the displayed total is base product + selected tier (matches SSR).
    const baseAddend = this._bundleBaseAddend();
    const displayPrice = baseAddend + tier.price;
    const displayCompare = baseAddend + tier.compareAt;
    const hasSale = tier.compareAt > tier.price;

    const currentEl = card.querySelector('.subscription-label-price');
    const compareEl = card.querySelector('.subscription-label-compare-at-price');

    if (currentEl) currentEl.textContent = formatMoney(displayPrice);
    if (compareEl) compareEl.textContent = hasSale ? formatMoney(displayCompare) : '';
    card.classList.toggle('has-sale', hasSale);

    // Discount badge next to the subscription title (bundle only).
    const saveBadge = document.querySelector('.subscription__subs .subscription-label-save-amount');
    if (saveBadge) {
      if (tier.save > 0) {
        saveBadge.textContent = `(${this.saveTemplate.replace('__AMOUNT__', formatMoney(tier.save))})`;
        saveBadge.classList.remove('hidden');
      } else {
        saveBadge.classList.add('hidden');
      }
    }

    const productInfoPrice = document.querySelector('.product-info__price');
    if (productInfoPrice) {
      const mainCurrent = productInfoPrice.querySelector('sale-price');
      const mainCompare = productInfoPrice.querySelector('compare-at-price');
      if (mainCurrent) {
        mainCurrent.textContent = formatMoney(displayPrice);
        mainCurrent.className = hasSale ? 'text-lg text-on-sale' : 'text-lg';
      }
      if (hasSale) {
        if (mainCompare) {
          mainCompare.textContent = formatMoney(displayCompare);
        } else if (mainCurrent) {
          const el = document.createElement('compare-at-price');
          el.className = 'text-subdued line-through';
          el.textContent = formatMoney(displayCompare);
          mainCurrent.after(el);
        }
      } else if (mainCompare) {
        mainCompare.remove();
      }
    }
  }

  _updateMobilePricing(tier) {
    const row = document.querySelector('.subscription__subs .subs-mobile-pricing');
    if (!row) return;
    const priceEl = row.querySelector('[data-mobile-price]');
    const compareEl = row.querySelector('[data-mobile-compare]');
    const saveEl = row.querySelector('[data-mobile-save]');
    const hasSale = tier.compareAt > tier.price;

    // For bundles the displayed total is base product + selected tier (matches SSR).
    const baseAddend = this._bundleBaseAddend();
    const displayPrice = baseAddend + tier.price;
    const displayCompare = baseAddend + tier.compareAt;

    if (priceEl) priceEl.textContent = formatMoney(displayPrice);
    if (compareEl) {
      if (hasSale) {
        compareEl.textContent = formatMoney(displayCompare);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (saveEl) {
      if (tier.save > 0) {
        const template = row.dataset.saveTemplate || 'Save __AMOUNT__';
        saveEl.textContent = template.replace('__AMOUNT__', formatMoney(tier.save));
        saveEl.hidden = false;
      } else {
        saveEl.hidden = true;
      }
    }
  }

  _updateTaglinePerMonth(tier) {
    const target = document.querySelector('.subscription__subs [data-tier-permonth]');
    if (!target) return;
    const wrapper = target.closest('[data-permonth-template]');
    const template = wrapper?.dataset.permonthTemplate || '__PRICE__';
    target.textContent = template.replace('__PRICE__', formatMoney(tier.perMonth));
  }

  _updateBuyButtonPrice(tier) {
    const form = this._getForm();
    if (!form) return;
    const btnText = form.querySelector('buy-buttons:not([data-preorder]) .button-inner');
    if (!btnText) return;

    let warrantyPrice = 0;
    const data = this._getPricesData();
    if (data) {
      const warrantyInput = document.getElementById('WarrantyCheckbox');
      if (warrantyInput && warrantyInput.checked) warrantyPrice = data.warrantyPrice || 0;
    }

    const total = this._bundleBaseAddend() + warrantyPrice + tier.price;
    const template = window.themeVariables?.strings?.addToCartBtnPrice || 'Add to cart - [price]';
    btnText.innerHTML = template.replace('[price]', formatMoney(total));
  }

  _dispatchSellingPlanChanged(sellingPlanId) {
    document.dispatchEvent(
      new CustomEvent('selling-plan-changed', { detail: { sellingPlanId } })
    );
  }
}

if (!window.customElements.get('subscription-tier-grid')) {
  window.customElements.define('subscription-tier-grid', SubscriptionTierGrid);
}
